import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ThumbsUp, ThumbsDown, Play, TrendingUp, Music2, MapPin, Globe } from "lucide-react";
import { useSmartMusicRecommendations, type LocationContext } from "@/hooks/useSmartMusicRecommendations";
import { usePlaceType, type PlaceType } from "@/hooks/usePlaceType";
import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { PlaceTypeSelector } from "./PlaceTypeSelector";

interface SmartRecommendationsProps {
  currentBPM?: number;
  mood?: string;
  timeOfDay?: string;
  weather?: string;
  onPlayTrack?: (track: string, artist: string) => void;
}

export const SmartRecommendations = ({
  currentBPM,
  mood,
  timeOfDay,
  weather,
  onPlayTrack,
}: SmartRecommendationsProps) => {
  const { recommendations, insights, isLoading, generateRecommendations, saveFeedback } = 
    useSmartMusicRecommendations();
  const { locationContext, isDetecting, detectPlaceType, setManualPlaceType, placeTypeIcons } = usePlaceType();
  const [feedbackGiven, setFeedbackGiven] = useState<Set<string>>(new Set());
  const [showLocationSelector, setShowLocationSelector] = useState(true);

  const handleGenerate = async () => {
    // Build location context for recommendations
    const location: LocationContext | undefined = locationContext ? {
      city: locationContext.city,
      neighborhood: locationContext.neighborhood,
      state: locationContext.state,
      country: locationContext.country,
      placeType: locationContext.placeType,
      coordinates: locationContext.coordinates,
    } : undefined;

    await generateRecommendations({
      bpm: currentBPM,
      mood,
      timeOfDay,
      weather,
      location,
    });
    
    setShowLocationSelector(false);
  };

  const handleFeedback = async (track: string, artist: string, feedback: 'like' | 'dislike') => {
    const key = `${track}-${artist}`;
    setFeedbackGiven(prev => new Set([...prev, key]));
    
    const location: LocationContext | undefined = locationContext ? {
      city: locationContext.city,
      neighborhood: locationContext.neighborhood,
      state: locationContext.state,
      country: locationContext.country,
      placeType: locationContext.placeType,
    } : undefined;

    await saveFeedback(track, artist, feedback, { 
      bpm: currentBPM, 
      mood, 
      timeOfDay, 
      weather,
      location,
    });
  };

  const handlePlaceTypeSelect = (placeType: PlaceType) => {
    setManualPlaceType(placeType);
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Recomendações Inteligentes</h3>
          <Badge variant="outline" className="gap-1">
            <Music2 className="w-3 h-3" />
            IA
          </Badge>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={isLoading}
          size="sm"
          className="gap-2"
        >
          <Sparkles className="w-4 h-4" />
          {isLoading ? 'Gerando...' : 'Gerar Recomendações'}
        </Button>
      </div>

      {/* Location Selector */}
      {showLocationSelector && (
        <PlaceTypeSelector
          selectedPlaceType={locationContext?.placeType}
          locationContext={locationContext}
          onSelect={handlePlaceTypeSelect}
          onDetectLocation={detectPlaceType}
          isDetecting={isDetecting}
        />
      )}

      {/* Show current location badge when not in selector mode */}
      {!showLocationSelector && locationContext?.placeType && (
        <button
          onClick={() => setShowLocationSelector(true)}
          className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg hover:bg-muted transition-colors text-sm"
        >
          <MapPin className="w-4 h-4 text-primary" />
          <span className="text-2xl">{placeTypeIcons[locationContext.placeType]}</span>
          <span className="text-muted-foreground">
            {locationContext.neighborhood || locationContext.city || 'Local selecionado'}
          </span>
          <Badge variant="secondary" className="ml-auto text-xs">
            Alterar
          </Badge>
        </button>
      )}

      {insights && (
        <div className="grid grid-cols-2 gap-3 p-4 bg-muted/30 rounded-lg">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">BPM Preferido</p>
            <p className="text-sm font-medium">{insights.preferred_bpm_range}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Energia</p>
            <p className="text-sm font-medium capitalize">{insights.energy_preference}</p>
          </div>
          <div className="col-span-2 space-y-1">
            <p className="text-xs text-muted-foreground">Gêneros Favoritos</p>
            <div className="flex flex-wrap gap-1">
              {insights.favorite_genres.map((genre, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {genre}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      <Separator />

      <ScrollArea className="h-[400px] pr-4">
        <div className="space-y-3">
          {recommendations.length === 0 && !isLoading && (
            <div className="text-center py-8 text-muted-foreground">
              <Sparkles className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Clique em "Gerar Recomendações" para descobrir músicas perfeitas para você</p>
            </div>
          )}

          {recommendations.map((rec, idx) => {
            const key = `${rec.track}-${rec.artist}`;
            const hasFeedback = feedbackGiven.has(key);

            return (
              <Card key={idx} className="p-4 space-y-3 hover:bg-accent/50 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium truncate">{rec.track}</h4>
                      {rec.discovery && (
                        <Badge variant="outline" className="text-xs gap-1">
                          <TrendingUp className="w-3 h-3" />
                          Descoberta
                        </Badge>
                      )}
                      {(rec as any).local_artist && (
                        <Badge variant="secondary" className="text-xs gap-1">
                          <Globe className="w-3 h-3" />
                          Local
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{rec.artist}</p>
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                      {rec.reason}
                    </p>
                  </div>
                  {onPlayTrack && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onPlayTrack(rec.track, rec.artist)}
                      className="shrink-0"
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Music2 className="w-3 h-3" />
                      {rec.bpm} BPM
                    </span>
                    <span>{rec.genre}</span>
                    <span>Energia: {Math.round(rec.energy * 100)}%</span>
                  </div>

                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={hasFeedback ? "secondary" : "ghost"}
                      onClick={() => handleFeedback(rec.track, rec.artist, 'like')}
                      disabled={hasFeedback}
                      className="h-7 w-7 p-0"
                    >
                      <ThumbsUp className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant={hasFeedback ? "secondary" : "ghost"}
                      onClick={() => handleFeedback(rec.track, rec.artist, 'dislike')}
                      disabled={hasFeedback}
                      className="h-7 w-7 p-0"
                    >
                      <ThumbsDown className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </ScrollArea>

      {insights && (
        <div className="pt-4 border-t space-y-2">
          <p className="text-xs text-muted-foreground">
            <strong>Padrão de Escuta:</strong> {insights.listening_patterns}
          </p>
          {(insights as any).location_influence && (
            <p className="text-xs text-muted-foreground">
              <strong>📍 Influência do Local:</strong> {(insights as any).location_influence}
            </p>
          )}
        </div>
      )}
    </Card>
  );
};
